"use client";

import { useTranslations } from "next-intl";
import React, { memo, Suspense } from "react";
import { BASE_URL } from "@/constant/apiPath";
import dynamic from "next/dynamic";
import Head from "next/head";
import { Skeleton, Typography } from "antd/lib";
import PriceForecast from "@/app/components/products/ProductForecast/PriceForecast";
import useAxiosQuery from "@/hooks/useMyQuery";
import KlardaPrediction from "@/app/components/KlardaPrediction";
import MyCard from "@/app/components/MyCard";
import ForecastFAQ from "@/app/components/products/ProductForecast/ForecastFAQ";
import ForecastContent from "@/app/components/products/ProductForecast/ForecastContent";

const PeopleAlsoWatch = dynamic(() => import("@/app/components/products/PeopleAlsoWatch"), { ssr: false });

const SEOHead = () => (
  <Head>
    <link rel="canonical" href={`${BASE_URL}/news`} />
    <title>Klarda Blogs - All About Crypto, Crypto News, Blockchain, Bitcoin</title>
    <meta
      name="description"
      content="Discover Klarda Blog to explore all about crypto, the latest crypto news; comprehensive technical and fundamental analysis; market insights; and regular recaps and reports. Enjoy a seamless user experience with our intuitive interface."
    />
    <meta name="image" content={`${BASE_URL}/seo/fb.jpg`} />
    <meta content={BASE_URL} property="og:url" />
    <meta content="website" property="og:type" />
    <meta content="Klarda: Crypto and Portfolio Intelligence" property="og:site_name" />
    <meta content={`${BASE_URL}/seo/fb.jpg`} property="og:image" />
    <meta content={BASE_URL} property="twitter:url" />
    <meta content="summary_large_image" property="twitter:card" />
    <meta content="Klarda: Crypto and Portfolio Intelligence" property="twitter:site" />
    <meta content={`${BASE_URL}/seo/twitter.jpg`} property="twitter:image" />
  </Head>
);
export interface IPriceFAQForecast {
  question: string;
  answer: string;
}

export interface IPriceRelatedForecast {
  symbol: string;
  name: string;
  logo_uri: string;
  price: number;
}

export interface IPositiveNegativeCorrelation {
  symbol: string;
  name: string;
  logo_uri: string;
  coefficient: number;
}

export interface IPriceCorrelation {
  positive: IPositiveNegativeCorrelation[];
  negative: IPositiveNegativeCorrelation[];
}

export interface IPriceSummary {
  mean: number;
  upper: number;
  lower: number;
}

export interface IPriceForecastSummary {
  d1: IPriceSummary;
  d7: IPriceSummary;
  m1: IPriceSummary;
  m3: IPriceSummary;
  m6: IPriceSummary;
  y1: IPriceSummary;
}

export interface ISummaryDetails {
  last_price: number;
  text: string;
  gauge: {
    status: string;
    value: number;
    statistics: {
      Buy: number;
      Neutral: number;
      Sell: number;
    };
  };
  prices: IPriceForecastSummary;
}
const ProductForecast = memo(({ symbol }: { symbol: string }) => {
  const t = useTranslations("products");
  const { data: response } = useAxiosQuery<{ data?: ISummaryDetails }>({
    url: "v1/product/forecast/summary",
    params: {
      symbol,
    },
  });
  return (
    <>
      <SEOHead />
      <main className="min-h-screen">
        <div className="flex flex-col gap-4">
          <MyCard title={t(`Price_Forecast`)}>
            <div className="flex flex-col gap-2">
              <Typography.Title level={5} className={"mb-0"}>
                {symbol} {t(`Price_Forecast`)}
              </Typography.Title>
              <Typography.Text>{response?.data?.text as string}</Typography.Text>
              <KlardaPrediction />
            </div>
          </MyCard>
          <PriceForecast symbol={symbol} />
          <ForecastContent symbol={symbol} />
          <ForecastFAQ symbol={symbol} />
          <Suspense fallback={<Skeleton active />}>
            <PeopleAlsoWatch symbol={symbol} />
          </Suspense>
        </div>
      </main>
    </>
  );
});

ProductForecast.displayName = "ProductForecast";
export default ProductForecast;
